import { TransactionListFooterComponent } from "./transaction-list-footer.component";
import { Component, inject, Input } from "@angular/core";
import { Router } from "@angular/router";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { PayoutProvider } from "@models";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { convertFiatToTradable } from "@helpers/tradable-convert";
import { FiatTransaction } from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { TooltipService } from "@main-services/tooltip.service";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { UserService } from "@main-services/user.service";
import { FiatWithdrawalModalComponent } from "@modules/main/components/step-summary/fiat-transactions/fiat-withdraw-modal/fiat-withdraw-modal.component";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { SendEntryPointService } from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { WalletBalance } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.model";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { LaunchDarklyService } from "@core-services/launch-darkly/launch-darkly.service";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { OrderFlow } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { take } from "rxjs/operators";
import { TinShieldEntryHelper } from "@modules/taxes/tin/services/tin-shield-entry.helper";
import { Derivative } from "@models/derivative/currencies/derivative";

@Component({
	selector: "fiat-transaction-list-footer",
	templateUrl: "./transaction-list-footer-template.component.html",
	styleUrls: ["./transaction-list-footer-template.component.scss"],
	standalone: false
})
export class FiatTransactionListFooterComponent extends TransactionListFooterComponent {
	@Input()
	public transaction: FiatTransaction;

	@Input()
	public balance: WalletBalance = null;

	private readonly tinShieldEntryHelper = inject(TinShieldEntryHelper);
	private readonly router = inject(Router);

	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected accountService: AccountService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected tooltipService: TooltipService,
		protected userService: UserService,
		protected contactService: ContactService,
		protected payaccountService: PayAccountService,
		protected masterdataService: MasterdataService,
		protected infoService: InfoService,
		protected translationService: TranslationService,
		protected readonly fiatDepositEntryService: FiatDepositEntryService,
		protected sendEntryPoint: SendEntryPointService,
		protected userFiatFundsService: UserFiatFundsService,
		protected readonly launchDarklyService: LaunchDarklyService,
		protected readonly checkoutEntryHelper: CheckoutEntryHelperService,
		protected readonly limitOrdersTrackingFacade: LimitOrdersTrackingFacade
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			tooltipService,
			userService,
			contactService,
			payaccountService,
			infoService,
			userFiatFundsService,
			fiatDepositEntryService,
			launchDarklyService,
			checkoutEntryHelper
		);
	}

	public get buttonLabel(): string {
		if (this.getTradeType().length > 0) {
			return (this.isFiatSend() ? "send" : this.getTradeType()) + "_more_list_item_label";
		}
		return "";
	}

	public setTransactionFiat(): void {
		this.transactionFiat = this.fiatService.getItemById(this.transaction.getFiatId());
	}

	public showFooterButtonWithdrawOrTradable(): boolean {
		if (this.router.url.includes("iframe")) {
			return false;
		}

		if (this.tradable instanceof Derivative) {
			return false;
		}

		if (this.transaction.isCard() || this.isTrxMarginTrading()) {
			return false;
		}
		if (this.tradableWallet instanceof IndexWallet) {
			// For index-wallets, the amount returned is always fiat, so we show the fiat-specific button! Savings never shows a button!
			return !this.transaction.isSavings;
		}

		if (!this.transaction.hasTrade()) {
			return (
				this.showButton() &&
				this.getTradeType() !== "withdrawal" &&
				this.getTradeType() !== "refund" &&
				this.getTradeType() !== "transfer"
			);
		}

		return (
			this.showButton() &&
			this.getTradeType() !== "withdrawal" &&
			this.getTradeType() !== "refund" &&
			!this.transaction.getTrade().isVoucher
		);
	}

	public showFooterButtonNOTWithdrawOrTradable(): boolean {
		if (this.router.url.includes("iframe")) {
			return false;
		}

		if (this.tradable instanceof Derivative) {
			return false;
		}

		if (this.transaction.isCard()) {
			return false;
		}

		if (this.tradableWallet instanceof IndexWallet) {
			// For index-wallets, the amount returned is always fiat, so we show the fiat-specific button! Savings never shows a button!
			return this.transaction.isSavings;
		}

		return (
			this.showButton() &&
			this.getTradeType() === "withdrawal" &&
			this.balance.operationalBalance >= +this.fiat.getMinWalletWithdraw()
		);
	}

	public tradeMore(useCase: string = "buy"): void {
		this.limitOrdersTrackingFacade.updateFlow(OrderFlow.TradeMoreHistory);
		super.tradeMore(useCase);
		if (this.disableActionButton(useCase)) {
			return;
		}

		if (useCase === "withdrawal" || useCase === "deposit") {
			const t = new FiatTransaction();
			t.setFiatWalletId(this.fiatWallet.getId());
			t.setDirectFiatTransaction(true);

			if (useCase === "withdrawal") {
				t["preselected"] = this.fiatWallet;
				t.setType("withdrawal");

				if (this.isFiatSend()) {
					this.sendEntryPoint.openFiatSendModal(t);
				} else {
					this.tinShieldEntryHelper
						.handleTinStatusCheck$()
						.pipe(take(1))
						.subscribe({
							next: (isTinFrozen) => {
								if (isTinFrozen) {
									return;
								}
								this.modalService.showModal(FiatWithdrawalModalComponent, { transaction: t });
							}
						});
				}
			} else if (useCase === "deposit") {
				const fiatPid = this.fiatService.findById(this._fiatWallet.getFiatId()).getPid() ?? "";
				this.fiatDepositEntryService.openDepositForWallet(this._fiatWallet.getId(), fiatPid);
			}
		}
	}

	public amountForButton(action: string): string {
		if (!this.disableActionButton(action)) {
			return "";
		}
		if (action === "withdrawal") {
			return this.getTransactionFiat().getMinWalletWithdraw();
		}
		if (!(this.tradableWallet instanceof IndexWallet) && (action === "sell" || action === "swap")) {
			return "" + convertFiatToTradable(this.fiat, this.tradable.getMinSell(), this.tradable);
		}
		if (action === "deposit") {
			return "";
		}
		return super.amountForButton(action);
	}

	public getToolTip(action: string): string {
		if (action === "withdrawal" && this.fiatWallet.getBalance() < +this.fiat.getMinWalletWithdraw()) {
			return "action_fiat_withdraw_below_min_tooltip";
		}
		return super.getToolTip(action);
	}

	public isFiatSend(): boolean {
		return this.getPayoutProvider() ? this.getPayoutProvider().sendEnabled(true) : false;
	}

	private getPayoutProvider(): PayoutProvider {
		const provider = this.masterdataService.payout_providers.find(
			(p) => p.getId() === this.transaction.getPayoutProviderId()
		);
		if (provider) {
			return provider;
		}
		return null;
	}
}
